import plur from "plur";
import * as yup from "yup";
import { Button, Flex, ModalV2, Text } from "@jsq/ds/core";
import { Form, FormSelect } from "@jsq/ds/forms";
import { SPACING } from "~/styles/variables";
import { type UserRow } from "..";
import { useRemoveWorkspaceAccess } from "./hooks";
import { formSelectWidth } from "./style_vanilla.css";

type RemoveWorkspaceAccessModalProps = {
  isOpen: boolean;
  onClose: () => void;
  users: UserRow[];
};

const validationSchema = yup.object().shape({
  arenaIds: yup
    .array()
    .required("Workspace is required")
    .min(1, "Please select at least one workspace"),
});

export const RemoveWorkspaceAccessModal = ({
  isOpen,
  onClose,
  users,
}: RemoveWorkspaceAccessModalProps) => {
  const { handleConfirm, isLoading, workspaceAccesses, initialValues } =
    useRemoveWorkspaceAccess({
      onClose,
      userIds: users.map((u) => u.id),
    });

  return (
    <ModalV2
      data-testid="remove-workspace-access-modal"
      title="Remove workspace access"
      description={`${users.length} ${plur("user", users.length)}`}
      show={isOpen}
      onHide={onClose}
    >
      <ModalV2.Body>
        <Form
          initialValues={initialValues}
          onSubmit={handleConfirm}
          yupValidationSchema={validationSchema}
        >
          <Flex flexDirection="column" gap={SPACING.X_SMALL}>
            <Text>Workspaces assigned to selected users</Text>
            <FormSelect
              multiple
              name="arenaIds"
              className={formSelectWidth}
              hideSelectAll
              hideSelectNone
              placeholder="Select workspace..."
              data-testid="workspace-select"
            >
              <FormSelect.Section>
                {workspaceAccesses.map((a) => (
                  <FormSelect.Option key={a.id} value={a.id}>
                    {`${a.name} (${a.id})`}
                  </FormSelect.Option>
                ))}
              </FormSelect.Section>
            </FormSelect>
          </Flex>

          <Text variant="regular-md" marginBottom="md">
            Are you sure you want to remove workspace access ?
          </Text>
          <Text variant="regular-md" marginBottom="xl">
            Any active work assigned to the selected users will be need to be unassigned.
          </Text>

          <Flex gap={SPACING.MEDIUM} justifyContent="end">
            <Button variant="neutral" onClick={onClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button variant="danger" type="submit" isLoading={isLoading} disabled={isLoading}>
              {`Remove ${users.length} ${plur("user", users.length)} from workspace`}
            </Button>
          </Flex>
        </Form>
      </ModalV2.Body>
    </ModalV2>
  );
};
